'use client';

import React, { useId, useMemo } from 'react';
import { cn, formatCurrency } from '@/lib/utils';
import { ChartSkeleton } from '@/components/charts/ChartSkeleton';
import { ChartHoverTooltip } from '@/components/charts/ChartHoverTooltip';
import { useChartScrub } from '@/components/charts/useChartScrub';
import type { OHLCV } from '@/lib/api-client';

/**
 * Compact intraday/multi-day price chart used by LiveGridTile, TickerPopup,
 * and any other dense-grid surface that wants the same look without the
 * weight of the full StockChart.
 *
 *   - Per-session line + area-fill segments. Pre-market and after-hours
 *     bars render in slate-gray so they don't claim the up/down color
 *     that belongs to the regular session. Daily bars (no time component)
 *     all classify as 'regular' and pick up the up/down color naturally.
 *   - Dashed reference line for the prior close when the value is
 *     inside the visible y-range; a small "↑ prev $X.XX" / "↓ prev"
 *     edge chip when it's not. Skip both by leaving prevClose undefined.
 *   - Optional right-edge price chip (clamped to the chart bounds so
 *     the bottom half doesn't clip past the body even on down days).
 *   - Optional L/H footer (low/high of the visible bar window).
 *
 * Geometry: SVG fills the parent (`w-full h-full`). The internal viewBox
 * is fixed (200x100) so all chart math stays in clean coordinates;
 * preserveAspectRatio="none" stretches it to whatever pixel size the
 * parent provides. The parent must constrain the height (e.g. flex-1
 * with min-h-0) — a viewBox-driven SVG without a height constraint will
 * grow to its intrinsic aspect ratio and overflow the layout.
 */

export interface MiniChartProps {
  /** `undefined` = not fetched yet (loading skeleton),
   *  `null`      = fetch returned no usable bars (empty skeleton),
   *  `OHLCV[]`   = render the chart. */
  bars: OHLCV[] | null | undefined;
  isUp: boolean;
  /** Prior trading day's close — drives the dashed baseline + edge chip. */
  prevClose?: number | null;
  /** Live price override for the right-edge chip (otherwise it shows the
   *  last bar's close). */
  livePrice?: number | null;
  showPriceLabel?: boolean;
  showFooter?: boolean;
  className?: string;
  /** Container background tint — defaults to the up/down color at low alpha
   *  so the chart panel reads as colored even when the line is short. */
  backgroundTint?: boolean;
  /** Render a crosshair + dot + price/date pill on pointer-move.
   *  Opt-in because dashboard tiles using MiniChart are typically too
   *  small for a useful tooltip; meant for the TickerPopup's 1D view. */
  showHover?: boolean;
}

type SessionKind = 'pre' | 'regular' | 'after';

const sessionOf = (date: string): SessionKind => {
  if (!date || !date.includes(' ')) return 'regular';
  const mm = parseInt(date.slice(11, 13), 10) * 60 + parseInt(date.slice(14, 16), 10);
  if (!Number.isFinite(mm)) return 'regular';
  if (mm < 9 * 60 + 30) return 'pre';
  if (mm > 16 * 60) return 'after';
  return 'regular';
};

const VIEW_W = 200;
const VIEW_H = 100;
const SESSION_GRAY = '#94a3b8';

export function MiniChart({
  bars, isUp,
  prevClose, livePrice,
  showPriceLabel = false,
  showFooter = false,
  backgroundTint = true,
  showHover = false,
  className,
}: MiniChartProps) {
  const id = useId().replace(/:/g, '_');
  const stroke = isUp ? '#10b981' : '#f43f5e';
  const fillTop = isUp ? 'rgba(16, 185, 129, 0.40)' : 'rgba(244, 63, 94, 0.40)';
  const fillBot = isUp ? 'rgba(16, 185, 129, 0.02)' : 'rgba(244, 63, 94, 0.02)';
  const tint = isUp ? 'rgba(16, 185, 129, 0.04)' : 'rgba(244, 63, 94, 0.04)';

  const usableBars = useMemo(() => {
    if (!bars || bars.length < 2) return null;
    const arr = bars
      .filter((b) => Number.isFinite(b.close) && b.close > 0)
      .map((b) => ({ ...b, session: sessionOf(b.date) }));
    return arr.length > 1 ? arr : null;
  }, [bars]);

  const closes = useMemo(() => usableBars?.map((b) => b.close) ?? null, [usableBars]);

  // 20% pad each side keeps the line clearly inset from the chart's top
  // and bottom edges. Anchored to closes only — including prev_close in
  // bounds wasted huge amounts of chart space on big gappers; prev_close
  // gets its own dashed line / edge chip below.
  const yRange = useMemo(() => {
    if (!closes || closes.length < 2) return null;
    const lo = Math.min(...closes);
    const hi = Math.max(...closes);
    const pad = Math.max(0.001, (hi - lo) * 0.20);
    return { lo: lo - pad, hi: hi + pad };
  }, [closes]);

  const closeY = (val: number): number => {
    if (!yRange) return VIEW_H;
    const range = yRange.hi - yRange.lo || 1;
    return VIEW_H - ((val - yRange.lo) / range) * VIEW_H;
  };

  const lineSegments = useMemo(() => {
    if (!usableBars || !yRange) return null;
    const stepX = VIEW_W / (usableBars.length - 1);
    const segs: { d: string; session: SessionKind }[] = [];
    let curSession: SessionKind | null = null;
    let cur = '';
    for (let i = 0; i < usableBars.length; i++) {
      const b = usableBars[i];
      const x = i * stepX;
      const y = closeY(b.close);
      if (curSession === null) { curSession = b.session; cur = `M ${x.toFixed(2)} ${y.toFixed(2)} `; }
      else if (b.session !== curSession) {
        cur += `L ${x.toFixed(2)} ${y.toFixed(2)} `;
        segs.push({ d: cur.trim(), session: curSession });
        curSession = b.session;
        cur = `M ${x.toFixed(2)} ${y.toFixed(2)} `;
      } else cur += `L ${x.toFixed(2)} ${y.toFixed(2)} `;
    }
    if (cur && curSession !== null) segs.push({ d: cur.trim(), session: curSession });
    return segs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usableBars, yRange]);

  const areaSegments = useMemo(() => {
    if (!usableBars || !yRange) return null;
    const stepX = VIEW_W / (usableBars.length - 1);
    const segs: { d: string; session: SessionKind }[] = [];
    let curSession: SessionKind | null = null;
    let firstX = 0;
    let cur = '';
    for (let i = 0; i < usableBars.length; i++) {
      const b = usableBars[i];
      const x = i * stepX;
      const y = closeY(b.close);
      if (curSession === null) { curSession = b.session; firstX = x; cur = `M ${x.toFixed(2)} ${y.toFixed(2)} `; }
      else if (b.session !== curSession) {
        cur += `L ${x.toFixed(2)} ${y.toFixed(2)} `;
        cur += `L ${x.toFixed(2)} ${VIEW_H.toFixed(2)} L ${firstX.toFixed(2)} ${VIEW_H.toFixed(2)} Z`;
        segs.push({ d: cur.trim(), session: curSession });
        curSession = b.session;
        firstX = x;
        cur = `M ${x.toFixed(2)} ${y.toFixed(2)} `;
      } else cur += `L ${x.toFixed(2)} ${y.toFixed(2)} `;
    }
    if (cur && curSession !== null) {
      const lastX = (usableBars.length - 1) * stepX;
      cur += `L ${lastX.toFixed(2)} ${VIEW_H.toFixed(2)} L ${firstX.toFixed(2)} ${VIEW_H.toFixed(2)} Z`;
      segs.push({ d: cur.trim(), session: curSession });
    }
    return segs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usableBars, yRange]);

  const prevCloseDisplay = useMemo(() => {
    if (prevClose == null || !yRange) return null;
    if (prevClose > yRange.lo && prevClose < yRange.hi) {
      return { kind: 'line' as const, y: closeY(prevClose), value: prevClose };
    }
    return {
      kind: 'edge' as const,
      direction: prevClose >= yRange.hi ? 'above' : 'below' as 'above' | 'below',
      value: prevClose,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevClose, yRange]);

  const lastTag = useMemo(() => {
    if (!closes || closes.length < 2) return null;
    return { y: closeY(closes[closes.length - 1]), value: closes[closes.length - 1] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closes, yRange]);

  const dayHL = useMemo(() => {
    if (!bars || bars.length === 0) return null;
    let hi = -Infinity, lo = Infinity;
    for (const b of bars) {
      if (Number.isFinite(b.high) && b.high > hi) hi = b.high;
      if (Number.isFinite(b.low) && b.low < lo && b.low > 0) lo = b.low;
    }
    if (!Number.isFinite(hi) || !Number.isFinite(lo)) return null;
    return { hi, lo };
  }, [bars]);

  const segmentStroke = (s: SessionKind) => (s === 'regular' ? stroke : SESSION_GRAY);
  const segmentFillId = (s: SessionKind) => (s === 'regular' ? `grad_${id}` : `grad_gray_${id}`);

  // ── Hover / touch-scrub state (opt-in via showHover) ──────────────
  // Shared with PopupChart so tap-to-read behaves the same on both.
  const { svgRef, index: hoverIdx, handlers: scrubHandlers, style: scrubStyle } = useChartScrub({
    viewW: VIEW_W,
    padLeft: 0,
    plotW: VIEW_W,
    count: usableBars?.length ?? 0,
    enabled: showHover,
  });

  const hoverBar = hoverIdx != null && usableBars ? usableBars[hoverIdx] : null;
  const hoverX = hoverIdx != null && usableBars && usableBars.length > 1
    ? (hoverIdx / (usableBars.length - 1)) * VIEW_W
    : 0;
  const hoverY = hoverBar ? closeY(hoverBar.close) : 0;

  return (
    <div className={cn('flex flex-col min-h-0', className)}>
      <div
        className="flex-1 min-h-0 relative"
        style={backgroundTint ? { background: tint } : undefined}
      >
        {lineSegments && lineSegments.length > 0 ? (
          <>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
              preserveAspectRatio="none"
              className="w-full h-full block"
              style={scrubStyle}
              {...scrubHandlers}
            >
              <defs>
                <linearGradient id={`grad_${id}`} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={fillTop} />
                  <stop offset="100%" stopColor={fillBot} />
                </linearGradient>
                <linearGradient id={`grad_gray_${id}`} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="rgba(148, 163, 184, 0.30)" />
                  <stop offset="100%" stopColor="rgba(148, 163, 184, 0.02)" />
                </linearGradient>
              </defs>
              {areaSegments?.map((seg, i) => (
                <path key={`area-${i}`} d={seg.d} fill={`url(#${segmentFillId(seg.session)})`} stroke="none" />
              ))}
              {lineSegments.map((seg, i) => (
                <path
                  key={i}
                  d={seg.d}
                  stroke={segmentStroke(seg.session)}
                  strokeWidth={1.75}
                  fill="none"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {prevCloseDisplay?.kind === 'line' && (
                <line
                  x1={0} x2={VIEW_W} y1={prevCloseDisplay.y} y2={prevCloseDisplay.y}
                  stroke="#94a3b8" strokeWidth={1} strokeDasharray="3 3" opacity={0.6}
                  vectorEffect="non-scaling-stroke"
                />
              )}
              {/* Hover crosshair + dot (only when showHover and pointer is over a bar) */}
              {showHover && hoverBar && (
                <>
                  <line
                    x1={hoverX} x2={hoverX} y1={0} y2={VIEW_H}
                    stroke="currentColor" strokeWidth={1} strokeDasharray="3 3"
                    vectorEffect="non-scaling-stroke" className="text-fg-subtle"
                  />
                  <circle
                    cx={hoverX} cy={hoverY} r={3.5}
                    fill={stroke} stroke="white" strokeWidth={1.5}
                    vectorEffect="non-scaling-stroke"
                  />
                </>
              )}
            </svg>
            {prevCloseDisplay?.kind === 'edge' && (
              <span
                className="absolute left-1 px-1 py-px rounded text-[9px] font-mono text-fg-subtle bg-card-bg/80 border border-default pointer-events-none"
                style={prevCloseDisplay.direction === 'above' ? { top: 2 } : { bottom: 2 }}
                title={`Prev close ${formatCurrency(prevCloseDisplay.value)} — ${prevCloseDisplay.direction === 'above' ? 'above' : 'below'} day's range`}
              >
                {prevCloseDisplay.direction === 'above' ? '↑' : '↓'} prev {formatCurrency(prevCloseDisplay.value)}
              </span>
            )}
            {showPriceLabel && lastTag && (
              <span
                className={cn(
                  'absolute right-1 px-1.5 py-px rounded text-[10px] font-mono font-semibold text-white pointer-events-none',
                  isUp ? 'bg-emerald-500' : 'bg-red-500',
                )}
                style={{
                  top: `clamp(10px, calc(${(lastTag.y / VIEW_H) * 100}% + 4px), calc(100% - 6px))`,
                  transform: 'translateY(-50%)',
                }}
              >
                {formatCurrency(livePrice ?? lastTag.value)}
              </span>
            )}
            {/* Hover/crosshair pill — rendered last so it stacks above the prev-close and live-price chips when they overlap. */}
            {showHover && hoverBar && svgRef.current && (
              <ChartHoverTooltip
                svg={svgRef.current}
                xPx={hoverX}
                viewW={VIEW_W}
                value={hoverBar.close}
                date={hoverBar.date}
              />
            )}
          </>
        ) : (
          <ChartSkeleton state={bars === null ? 'empty' : 'loading'} />
        )}
      </div>
      {showFooter && dayHL && (
        <div className="px-2 pt-1 flex items-center justify-between text-[10px] text-fg-subtle font-mono flex-shrink-0">
          <span>L {formatCurrency(dayHL.lo)}</span>
          <span>H {formatCurrency(dayHL.hi)}</span>
        </div>
      )}
    </div>
  );
}
